import { useEffect, useRef, useState } from "react";

import { describeTidyStayed } from "@cleo/shared/sessionTidy";

import { randomUUID } from "~/lib/utils";
import {
  previewSessionTidy,
  type SessionTidyInput,
  type SessionTidyPreview,
} from "~/neucleos/sessionTidyClient";
import type { SessionTidyRunView } from "~/neucleos/useSessionTidyRun";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "./ui/dialog";

/** Can 80: how long a session must be quiet before a tidy archives it. */
export const SESSION_TIDY_DAY_CHOICES = [1, 3, 7, 14, 30] as const;

const daysWords = (days: number) => (days === 1 ? "a day" : `${days} days`);

/**
 * A card's rooms, by kind, as the tidy judges each candidate's own room: so a
 * room whose sessions all folded under Earlier is still the card's. Null
 * tidies every room.
 */
export type SessionTidyScope =
  | { readonly kind: "general" }
  | { readonly kind: "workroom"; readonly workroomKey: string };

const scopeKeyOf = (scope: SessionTidyScope | null) =>
  scope === null ? "*" : scope.kind === "general" ? "general" : `workroom:${scope.workroomKey}`;
const scopeInputOf = (key: string) =>
  key === "general"
    ? { general: true }
    : key.startsWith("workroom:")
      ? { workroomKey: key.slice("workroom:".length) }
      : {};

/**
 * Can 80 (the principal, 17 Oct 2026: "Maybe when selected, it asks user how
 * many days old to archive?"): pick the days, see what goes and what stays,
 * then archive the person's own quiet sessions in one confirmation. The
 * confirmation carries the previewed count; a different count archives nothing.
 * The tidy itself is the rail's (useSessionTidyRun, W-105e4e0a): this dialog
 * can be closed while it runs, and opened again to see where it stands.
 */
export function SessionTidyDialog({
  worldKey,
  scope,
  roomLabel,
  keep,
  initialDays,
  run,
  onArchive,
  onClose,
}: {
  readonly worldKey: string;
  /** The card's rooms, or every room when null. */
  readonly scope: SessionTidyScope | null;
  readonly roomLabel: string | null;
  readonly keep: ReadonlyArray<string>;
  readonly initialDays: number;
  /** The rail's tidy running or just finished, or null. */
  readonly run: SessionTidyRunView | null;
  /** The person's one approval: the tidy with the previewed count. */
  readonly onArchive: (
    input: SessionTidyInput & { readonly operationId: string; readonly count: number },
  ) => void;
  readonly onClose: () => void;
}) {
  const [days, setDays] = useState(initialDays);
  const live = run?.outcome === null ? run : null;
  const running = live !== null;
  const outcome = run?.outcome ?? null;
  const hasRun = run !== null;
  // One stable identity per confirmed tidy, so a repeat resumes it.
  const operationId = useRef(randomUUID());
  // Keyed by content: the rail rebuilds these on every render. Session ids
  // never hold a comma (S_ ids), so the keys round-trip.
  const scopeKey = scopeKeyOf(scope);
  const keepKey = keep.join(",");
  const previewKey = `${days}|${scopeKey}|${keepKey}`;
  const [answer, setAnswer] = useState<{
    readonly key: string;
    readonly preview: SessionTidyPreview;
  } | null>(null);
  useEffect(() => {
    // Not while a tidy runs, nor over its result: the preview would read every
    // candidate's entry again beside the archive.
    if (hasRun) return;
    let current = true;
    void previewSessionTidy({
      worldKey,
      days,
      ...scopeInputOf(scopeKey),
      keep: keepKey === "" ? [] : keepKey.split(","),
    }).then((next) => {
      if (current) setAnswer({ key: `${days}|${scopeKey}|${keepKey}`, preview: next });
    });
    return () => {
      current = false;
    };
  }, [worldKey, days, scopeKey, keepKey, hasRun]);
  const preview = answer?.key === previewKey ? answer.preview : null;
  const count = preview?.ok === true ? preview.count : 0;
  const archive = () => {
    if (preview?.ok !== true || count === 0 || running) return;
    onArchive({
      worldKey,
      days,
      ...scopeInputOf(scopeKey),
      keep,
      count,
      operationId: operationId.current,
    });
  };
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogPopup className="w-full max-w-md" data-session-tidy="">
        <DialogHeader>
          <DialogTitle>
            {roomLabel === null ? "Tidy up your rooms" : `Tidy up ${roomLabel}`}
          </DialogTitle>
          <DialogDescription>
            Archive your own sessions that have been quiet. Pinned sessions, ones working or waiting
            on you, and ones with a hand still in use stay.
          </DialogDescription>
        </DialogHeader>
        <DialogPanel className="space-y-4">
          {outcome === null ? (
            <>
              <label className="flex flex-col gap-1.5 text-xs">
                Archive sessions quiet for
                <select
                  aria-label="Quiet for"
                  className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                  data-session-tidy-days=""
                  disabled={running}
                  value={days}
                  onChange={(event) => {
                    operationId.current = randomUUID();
                    setDays(Number(event.target.value));
                  }}
                >
                  {SESSION_TIDY_DAY_CHOICES.map((choice) => (
                    <option key={choice} value={choice}>
                      {daysWords(choice)} or more
                    </option>
                  ))}
                </select>
              </label>
              <div aria-live="polite" className="space-y-1 text-xs" data-session-tidy-preview="">
                {preview === null ? (
                  running ? null : (
                    <p className="text-muted-foreground">Looking for quiet sessions…</p>
                  )
                ) : preview.ok ? (
                  <>
                    <p className="font-medium text-foreground" data-session-tidy-title="">
                      {preview.words.title}
                    </p>
                    {preview.words.lines.map((line) => (
                      <p className="text-muted-foreground" key={line}>
                        {line}
                      </p>
                    ))}
                  </>
                ) : (
                  <p role="alert">{preview.reason}</p>
                )}
                {running ? (
                  <p className="text-foreground" data-session-tidy-running="">
                    Archiving in the background; the rail updates as they go.
                  </p>
                ) : null}
              </div>
            </>
          ) : outcome.ok ? (
            <div className="space-y-1 text-xs" data-session-tidy-result="">
              <p className="font-medium text-foreground">
                Archived {outcome.archived.length} session{outcome.archived.length === 1 ? "" : "s"}
                .
              </p>
              {outcome.skipped.length > 0 ? (
                <>
                  <p className="text-muted-foreground" data-session-tidy-stayed="">
                    {outcome.skipped.length} stayed:
                  </p>
                  {/* W-9b736ece: which stayed, as the rail names each and its card, and why. */}
                  <ul className="list-disc space-y-0.5 pl-4 text-muted-foreground">
                    {describeTidyStayed(outcome.skipped).map((line) => (
                      <li data-session-tidy-stayed-session="" key={line.key}>
                        {line.text}
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}
              {outcome.remaining > 0 ? (
                <p className="text-muted-foreground">
                  {outcome.remaining} more wait for the next tidy.
                </p>
              ) : null}
            </div>
          ) : (
            <p className="text-xs" role="alert" data-session-tidy-refused="">
              {outcome.reason}
            </p>
          )}
        </DialogPanel>
        <DialogFooter>
          {outcome === null ? (
            <>
              <Button data-session-tidy-close="" onClick={onClose} variant="ghost">
                {running ? "Close" : "Cancel"}
              </Button>
              <Button
                data-session-tidy-confirm=""
                disabled={running || preview?.ok !== true || count === 0}
                onClick={archive}
              >
                {live !== null
                  ? `Archiving… ${live.archived} of ${live.total ?? live.count}`
                  : preview === null
                    ? "Looking…"
                    : count === 0
                      ? "Nothing to archive"
                      : `Archive ${count} session${count === 1 ? "" : "s"}`}
              </Button>
            </>
          ) : (
            <Button onClick={onClose}>Done</Button>
          )}
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}
